import { Chip, Divider, Group, Kbd, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { FilterHeader } from 'src/common/components/table/SearchHeader';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import classes from './SecretMenuIndex.module.css';
import SecretMenuToolMarkers from './SecretMenuToolMarkers';
import {
  filterSecretMenuTools,
  SECRET_MENU_CATEGORIES,
  SecretMenuAudience,
  SecretMenuMarker,
  SecretMenuTool,
} from './secretMenuCatalogue';

type SecretMenuIndexProps = {
  tools: readonly SecretMenuTool[];
};

export default function SecretMenuIndex({ tools }: SecretMenuIndexProps) {
  const [query, setQuery] = useState('');
  const [audiences, setAudiences] = useState<SecretMenuAudience[]>([]);
  const [markers, setMarkers] = useState<SecretMenuMarker[]>([]);

  const matches = filterSecretMenuTools(tools, { query, audiences, markers });
  const isFiltered = matches.length !== tools.length;

  return (
    <Stack gap="xl" p="lg">
      <Stack gap="sm">
        <Title order={2}>Secret menu</Title>
        <Text size="textSm" c="dimmed">
          Press <Kbd>Cmd</Kbd> + <Kbd>K</Kbd> from any tool to jump to another one.
        </Text>
        <Group gap="sm" align="center">
          <FilterHeader
            searchTerm={query}
            setSearchTerm={setQuery}
            placeholder="Search tools"
            inputProps={{ autoFocus: true }}
          />
          <Chip.Group
            multiple
            value={audiences}
            onChange={values => setAudiences(values as SecretMenuAudience[])}
          >
            <Group gap="xxs">
              <Chip value="ops">Ops</Chip>
              <Chip value="dev">Dev</Chip>
            </Group>
          </Chip.Group>
          <Divider orientation="vertical" />
          <Chip.Group
            multiple
            value={markers}
            onChange={values => setMarkers(values as SecretMenuMarker[])}
          >
            <Group gap="xxs">
              <Chip value="dangerous">Dangerous</Chip>
              <Chip value="deprecated">Deprecated</Chip>
            </Group>
          </Chip.Group>
        </Group>
        <Text size="textSm" c="dimmed">
          {isFiltered ? `${matches.length} of ${tools.length} tools` : `${tools.length} tools`}
        </Text>
      </Stack>

      {matches.length === 0 && (
        <Text size="textSm">No tools match that search and those filters.</Text>
      )}

      {SECRET_MENU_CATEGORIES.map(category => {
        const categoryTools = matches.filter(tool => tool.category === category.id);
        if (categoryTools.length === 0) return null;
        return (
          <Stack key={category.id} gap="sm">
            <Stack gap={0}>
              <Text size="textLg" fw={FONT_WEIGHTS.semibold}>
                {category.title}
              </Text>
              <Text size="textSm" c="dimmed">
                {category.blurb}
              </Text>
            </Stack>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
              {categoryTools.map(tool => (
                <ToolCard key={tool.slug} tool={tool} />
              ))}
            </SimpleGrid>
          </Stack>
        );
      })}
    </Stack>
  );
}

function ToolCard({ tool }: { tool: SecretMenuTool }) {
  return (
    <Paper component={Link} to={tool.href} variant="card" className={classes.card} p="sm">
      <Stack gap="xxxs">
        <Group gap="xxs" wrap="wrap">
          <Text size="textMd" fw={FONT_WEIGHTS.semibold}>
            {tool.label}
          </Text>
          <SecretMenuToolMarkers tool={tool} />
        </Group>
        <Text size="textSm" c="dimmed">
          {tool.description}
        </Text>
      </Stack>
    </Paper>
  );
}
